import { useMemo, useState } from 'react';

export type SortDirection = 'asc' | 'desc';
export type SortState<K extends string> = { key: K; direction: SortDirection } | null;

type SortableValue = string | number | boolean | null | undefined;
const collator = new Intl.Collator('es-VE', { numeric: true, sensitivity: 'base' });

function compareValues(a: SortableValue, b: SortableValue): number {
  if (a === b) return 0;
  if (a === null || a === undefined) return 1;
  if (b === null || b === undefined) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b);
  return collator.compare(String(a), String(b));
}

/**
 * Ordena en cliente la página visible. El backend sigue paginando; esto es
 * para que el operador pueda leer una tabla densa sin salir de la pantalla.
 */
export function useTableSort<T, K extends string>(
  rows: T[],
  getters: Record<K, (row: T) => SortableValue>,
  initialKey?: K,
  initialDirection: SortDirection = 'asc',
) {
  const [sort, setSort] = useState<SortState<K>>(
    initialKey ? { key: initialKey, direction: initialDirection } : null,
  );

  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    const getValue = getters[sort.key];
    const factor = sort.direction === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => compareValues(getValue(a), getValue(b)) * factor);
  }, [getters, rows, sort]);

  const toggleSort = (key: K) => {
    setSort((current) =>
      current?.key === key
        ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' },
    );
  };

  return { rows: sortedRows, sort, toggleSort };
}
